var __makeTemplateObject = (this && this.__makeTemplateObject) || function (cooked, raw) {
    if (Object.defineProperty) { Object.defineProperty(cooked, "raw", { value: raw }); } else { cooked.raw = raw; }
    return cooked;
};
var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { connect } from '../../store/connect';
import noop from 'lodash/noop';
import debounce from "lodash/debounce";
import { FLEX_COLUMN, INPUT_GROUP, INPUT, IS_INVALID, INPUT_DESCRIPTION, VALIDATION_MESSAGE_HEADER, TEXT_INPUT, SELECT, SEARCH_SELECT_SEARCH_QUERY_CONTAINER } from '../../classConstants';
import FormRender from '@data-driven-forms/react-form-renderer/dist/cjs/form-renderer';
import { Form as FinalForm } from '@data-driven-forms/react-form-renderer';
import useFormApi from '@data-driven-forms/react-form-renderer/dist/cjs/use-form-api';
import { updateForm } from "../../store/actions";
import { NotificationList } from '../Notification/NotificationsList';
import { useDispatch } from 'react-redux';
var isDisabled = function (disableStates, getState) { return disableStates.map(function (item) { return getState()[item]; }).find(function (item) { return !!item; }); };
var FormTemplate = function (_a) {
    var _b = _a.schema, _c = _b.title, title = _c === void 0 ? '' : _c, _d = _b.description, description = _d === void 0 ? '' : _d, _e = _b.validationErrors, validationErrors = _e === void 0 ? [] : _e, children = _b.children, formFields = _a.formFields;
    var _f = useFormApi(), handleSubmit = _f.handleSubmit, getState = _f.getState, onReset = _f.onReset, onCancel = _f.onCancel;
    return (React.createElement(FinalForm, { onSubmit: handleSubmit, keepDirtyOnReinitialize: true, render: function (props) {
            var reset = props.form.reset;
            return React.createElement(Form, { onSubmit: handleSubmit },
                React.createElement(NotificationList, { shouldScrollToRef: false, items: validationErrors }),
                title && React.createElement("h2", null, title),
                description && React.createElement("p", null, description),
                formFields,
                children);
        } }));
};
var BasicForm = function (props) {
    return (React.createElement("form", __assign({ className: "ws-form" }, props, { noValidate: true })));
};
var mapStateToProps = function (state, _a) {
    var _b = _a.name, name = _b === void 0 ? '' : _b;
    if (!name)
        return {};
    return (state.formReducer.forms || {})[name] || {};
};
var formFactory = function () {
    var Form = function (_a) {
        var _b = _a.name, name = _b === void 0 ? '' : _b, initialValues = _a.initialValues, schema = _a.schema, componentMapper = _a.componentMapper, validatorMapper = _a.validatorMapper, _c = _a.clearedValue, clearedValue = _c === void 0 ? '' : _c, children = _a.children, onSubmit = _a.onSubmit, onCancel = _a.onCancel;
        var unmountRef = React.useRef(false);
        var dispatch = useDispatch();
        // No debounce causes delayed typing.
        var debug = useCallback(debounce(function (x) {
            var _a;
            if (!name || !updateForm || !dispatch)
                return;
            if (unmountRef.current)
                return;
            if ((_a = x.values) === null || _a === void 0 ? void 0 : _a.skip) {
                return;
            }
            dispatch(updateForm({
                name: name,
                form: x.values
            }, "form update"));
        }, 100, { leading: true }), [name]);
        useEffect(function () { return function () {
            unmountRef.current = true;
            debug.cancel();
        }; }, [debug]);
        return (React.createElement(FormRender, { componentMapper: componentMapper, validatorMapper: validatorMapper, FormTemplate: ReactiveFormWithStyles, children: children, schema: schema, clearedValue: clearedValue, debug: debug, initialValues: initialValues, onSubmit: onSubmit || noop, onCancel: onCancel || noop }));
    };
    return connect(mapStateToProps)(Form);
};
export var ReactiveForm = formFactory();
var FormTemplateCanReset = function (props) {
    return React.createElement(FormTemplate, __assign({}, props, { canReset: true }));
};
var formStyles = "\n    max-width: 100%;\n    ." + VALIDATION_MESSAGE_HEADER + " {\n        margin: 15px 0 10px;\n        &:first-of-type { margin-top: 0; }\n    }\n\n    ." + TEXT_INPUT + ", ." + SELECT + " {\n        padding: 5px;\n        min-height: var(--ws-input-min-height, 20px);\n        outline: none;\n        border-style: solid;\n        border-width: 1px;\n\n        border-color: var(--ws-input-border-color);\n        border-radius: var(--ws-input-border-radius);\n\n        background-color: transparent;\n        color: inherit;\n        background-color: var(--ws-input-bg, none);\n        color: var(--ws-input-color, inherit);\n\n        &:disabled {\n            background-color: #fff;\n            color: #ccc;\n            opacity: 0.5;\n\n            background-color: var(--ws-input-disabled-bg, #fff);\n            color: var(--ws-input-disabled-color, #ccc);\n            opacity: var(--ws-input-disabled-opacity, 0.5);\n        }\n\n        &.ws-textarea {\n            min-height: 100px;\n        }\n    }\n\n    ." + IS_INVALID + " ." + INPUT + " {\n        border-color: #a94442;\n        border-color: var(--ws-danger-text);\n    }\n\n    ." + FLEX_COLUMN + " > ." + TEXT_INPUT + ",\n    ." + FLEX_COLUMN + " > ." + SELECT + ",\n    ." + SEARCH_SELECT_SEARCH_QUERY_CONTAINER + " ." + TEXT_INPUT + " {\n        width: 100%;\n    }\n\n    ." + INPUT_GROUP + " {\n        margin-bottom: 1em;\n        margin-bottom: var(--ws-input-margin);\n\n        label {\n            margin-bottom: 0;\n        }\n        ." + INPUT_DESCRIPTION + " {\n            margin-top: .5em;\n        }\n    }\n";
export var ReactiveFormWithStyles = styled(FormTemplateCanReset)(templateObject_1 || (templateObject_1 = __makeTemplateObject(["", ""], ["", ""])), formStyles);
export var Form = styled(BasicForm)(templateObject_2 || (templateObject_2 = __makeTemplateObject(["", ""], ["", ""])), formStyles);
var templateObject_1, templateObject_2;
